<template>
  <div>
    <suggestion-input
      v-model="searchKey"
      :list="driverList"
      :lookup-fields="['name', 'number']"
      accessor="name"
      placeholder="Search drivers by name or mobile number"
      icon-class="fa-search"
      icon-position="left"
      class="absolute z-20"
      style="width: 320px; top: 60px; margin-left: 10px; background: white; border-radius: 4px;"
      @autocomplete-item-set="onDriverSelect"
      @autocomplete-cleared="resetMap"
    />
    <div
      v-if="selectedDriver && selectedDriver.name"
      class="absolute z-10"
      style="width: 320px; top: 110px; margin-left: 10px; background: white; border-radius: 4px; padding: 10px;">
      <div class="bg-white flex">
        <pt-avatar
          :type="selectedDriver['profile_pic'] ? 'image' : 'icon'"
          :src="`https://cdn.pathao.com${selectedDriver['profile_pic']}`"
          size="xs"
          class="mr-4"
        />
        <div class="flex flex-1 flex-col">
          <div class="flex-1 text-lg font-bold pb-2">
            {{ selectedDriver.name }}
            <i
              class="fa fa-close pull-right cursor-pointer"
              @click="resetMap"
            />
          </div>
          <div class="flex-1 flex">
            <div class="flex w-24 uppercase items-center">
              <div :class="['w-2 h-2 mr-2 rounded-full', getIndicatorByStatus(selectedDriver.status)]" />
              {{ selectedDriver.status.toLowerCase() === 'started' ? 'ON TRIP' : selectedDriver.status.toUpperCase() }}
            </div>
            <div class="flex h-full flex-1 items-center">
              <i class="fa fa-phone mr-1"/>
              {{ selectedDriver.number }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      :style="{ width: '320px', top: selectedDriver && selectedDriver.name ? '200px' : '110px', left: '10px', padding: '10px' }"
      class="flex z-10 absolute bg-white rounded"
    >
      <div class="w-1/3 mr-2">
        <pt-form-group
          label=""
          label-for="onlineMarker"
        >
          <pt-form-checkbox
            id="onlineMarker"
            v-model="showOnlineMarkers"
          >Online({{ driverCounter.online }})
          </pt-form-checkbox>
        </pt-form-group>
      </div>
      <div class="w-1/3 mr-2">
        <pt-form-group
          label=""
          label-for="onTripMarker">
          <pt-form-checkbox
            id="onTripMarker"
            v-model="showOnTripMarkers"
          >OnTrip({{ driverCounter.ontrip }})</pt-form-checkbox>
        </pt-form-group>
      </div>
      <div class="w-1/3">
        <pt-form-group
          label=""
          label-for="offlineMarker">
          <pt-form-checkbox
            id="offlineMarker"
            v-model="showOfflineMarkers">Offline({{ driverCounter.offline }})</pt-form-checkbox>
        </pt-form-group>
      </div>
    </div>
    <gmap-map
      ref="liveMap"
      :id="name"
      :center="center"
      :zoom="zoom"
      style="width:100%;  height: 660px;"
    >
      <gmap-marker
        v-for="(driver, driverIndex) in markers"
        :ref="`mapMarker-${driver.id}`"
        :key="driverIndex"
        :position="{ lat: driver.lat, lng: driver.lon }"
        :clickable="true"
        :icon="getMarkerIcon(driver.status, driver.id)"
        @click="setDriverInMap(driver)"
      />
    </gmap-map>
    <div class="hidden">{{ faker }}</div>
  </div>
</template>

<script>
import filter from 'lodash/filter';
import SuggestionInput from '@components/custom/SuggestionInput';
import PtAvatar from '../Avatar';
import { PtButton } from '@pathao/ui-components/esm/Button';
import { PtFormCheckbox, PtFormGroup } from '@pathao/ui-components/esm/FormComponents';

const ZOOM_BASE = 12;
const ZOOM_MAX = 15;

export default {
  name: 'GoogleMap',
  components: { PtAvatar, SuggestionInput, PtButton, PtFormCheckbox, PtFormGroup },
  props: {
    name: {
      type: String,
      required: true,
    },
    driversInfo: {
      type: Array,
      required: true,
    },
    driverList: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      searchKey: null,
      zoom: ZOOM_BASE,
      selectedDriver: null,
      center: {
        lat: 23.777176,
        lng: 90.399452,
      },
      isViewingSearchedUser: false,
      faker: 0,
      showOnlineMarkers: true,
      showOfflineMarkers: false,
      showOnTripMarkers: true,
    };
  },
  computed: {
    markers() {
      const markers = [];
      this.driversInfo.forEach(location => {
        const hasLatLon = location.lat && location.lon;
        const matchesOnlineStatus = this.showOnlineMarkers && location.status === 'online';
        const matchesOfflineStatus = this.showOfflineMarkers && location.status === 'offline';
        const matchesOnTripStatus = this.showOnTripMarkers && location.status === 'ontrip';
        if (hasLatLon && (matchesOnlineStatus || matchesOfflineStatus || matchesOnTripStatus)) {
          markers.push(location);
        }
      });
      return markers;
    },
    driverCounter() {
      const counter = {
        online: 0,
        offline: 0,
        ontrip: 0,
      };

      this.driversInfo.forEach(location => {
        if (counter.hasOwnProperty(location.status)) {
          counter[location.status] += 1;
        }
      });

      return counter;
    },
  },
  watch: {
    markers() {
      this.setBoundWithAllMarkers();
    },
  },
  methods: {
    setBoundWithAllMarkers() {
      this.$refs.liveMap.$mapPromise.then((map) => {
        // eslint-disable-next-line
        const bounds = new google.maps.LatLngBounds();
        this.markers.forEach(marker => {
          bounds.extend({
            lat: marker.lat,
            lng: marker.lon,
          });
        });

        if (!bounds.isEmpty()) {
          this.center = bounds.getCenter();
          map.panToBounds(bounds);
          if (this.markers.length > 1) {
            map.fitBounds(bounds);
          }
        }

        this.zoom = ZOOM_BASE;
      });
    },
    onDriverSelect(driver) {
      const matched = filter(this.driversInfo, (dr) => {
        return dr.id === driver.id;
      });

      if (matched && matched.length) {
        this.isViewingSearchedUser = true;
        this.setDriverInMap(matched[0]);
      } else {
        this.resetMap();
      }
    },
    resetMap(driver) {
      this.selectedDriver = driver || {};
      this.searchKey = null;
      this.zoom = ZOOM_BASE;
      this.isViewingSearchedUser = false;
      this.setBoundWithAllMarkers();
    },
    setDriverInMap(driver) {
      this.selectedDriver = driver;

      if (this.isViewingSearchedUser && driver.lat && driver.lon) {
        this.$refs.liveMap.$forceUpdate();
        this.center = {
          lat: driver.lat,
          lng: driver.lon,
        };
        this.zoom = ZOOM_MAX;
      }
    },
    getMarkerIcon(driverStatus, driverId) {
      const isSelectedDriver = this.selectedDriver && this.selectedDriver.id === driverId;
      const showIconInFullOpacity = (isSelectedDriver && this.isViewingSearchedUser) || !isSelectedDriver || !this.isViewingSearchedUser;
      let markerFile;

      switch (driverStatus.toLowerCase()) {
        case 'ontrip':
          markerFile = showIconInFullOpacity ? 'map_marker_on_trip.png' : 'map_marker_on_trip_50.png';
          break;
        case 'offline':
          markerFile = showIconInFullOpacity ? 'map_marker_offline.png' : 'map_marker_offline_50.png';
          break;
        case 'online':
        default:
          markerFile = showIconInFullOpacity ? 'map_marker_online.png' : 'map_marker_online_50.png';
          break;
      }

      return require(`../../../assets/images/${markerFile}`);
    },
    getIndicatorByStatus(status) {
      let indicator;

      switch (status.toLowerCase()) {
        case 'ontrip':
          indicator = 'bg-green';
          break;

        case 'offline':
          indicator = 'bg-red';
          break;

        case 'online':
        default:
          indicator = 'bg-blue';
          break;
      }

      return indicator;
    },
  },
};
</script>
